import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBox, faEye } from '@fortawesome/free-solid-svg-icons';
import { convertTimestampToDateTime } from '../../../helper/helper';
import { Link, useNavigate } from 'react-router-dom';
import { Toast } from '../../../components/Common/Toast';
import SearchInput from '../../../components/Common/SearchInput';
import TableHeader from '../../../components/Common/TableHeader';
import PaginationButtons from '../../../components/Common/Pagination';
import TableInfoBar from '../../../components/Admin/TableInfoBar';
import Dialogs from '../../../components/Common/Dialog';
import { OrderDetailsModel } from '../../../interfaces/order.interface';
import { UserModel, UserTypeActivityTitle } from '../../../interfaces/user.interface';
import { fetchUserOrders, shipSelectedOrder } from './services/PhysicalBookOrder.service';
import { TokenCookieManager } from '../../../utils/tokenCookies';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import NoItemsFound from '../../../components/NoItemsFound/NoItemsFound';
import OrderDetailsTable from './components/OrderDetailsTable';
import Loader from '../../../components/Common/Loader';
import { orderTypeLabel, orderItemLabel, orderRouteLabel, getSyncState, isPhysicalOrder, syncStateLabel, SYNC_STATE_BADGE, Tone, calculateOrderTotal } from './helpers/orderDisplay';
import FulfillmentStatusBadges, { Badge } from './components/FulfillmentStatusBadges';

interface PhysicalBookOrderProps {
  selectedUserEmail: string;
  user: UserModel | null;
  setSelectedUserEmail: (email: string | null) => void;
  onOptionClick: (option: string) => void;
}

const PhysicalBookOrder = ({
                             selectedUserEmail,
                             user,
                             setSelectedUserEmail,
                             onOptionClick
                           }: PhysicalBookOrderProps) => {
  const [loader, setLoader] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState<number | null>(null);
  const [order, setOrder] = useState<OrderDetailsModel[]>([]);
  const [viewBooks, setViewBooks] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserModel | null>(null);
  const [selectedOrderDetails, setSelectedOrderDetails] = useState<OrderDetailsModel | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!TokenCookieManager.hasToken()) {
      navigate('/admin');
    }
  });

  const handleFetchUserOrders = async () => {
    try {
      if (!selectedUserEmail) {
        Toast('User email is required to fetch orders', true);
        return;
      }
      setLoader(true);
      const { orders, totalPages } = await fetchUserOrders(selectedUserEmail, currentPage);
      setOrder(orders);
      setTotalPages(totalPages);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    if (selectedUserEmail) handleFetchUserOrders();
  }, [selectedUserEmail, currentPage]);

  const handlePageChange = (page: number | any) => {
    if (page > 0 && page <= totalPages!) setCurrentPage(+page);
  };

  const openDialog = (user: UserModel) => {
    if (user) {
      setSelectedUser(user);
      setViewBooks(true);
    }
  };
  const closeDialog = () => {
    setViewBooks(false);
    setSelectedUser(null);
  };

  const ship = async (selectedOrder: OrderDetailsModel) => {
    if (!selectedOrder?.orderNumber) {
      return;
    }
    await shipSelectedOrder(selectedOrder.orderNumber, user?.email, (loader) => setOrder((prevState) => {
      return prevState.map(item => item.orderNumber === selectedOrder.orderNumber ? {
        ...item,
        shipLoading: loader
      } : item);
    }));
  };

  const orderDetail = (order: OrderDetailsModel) => {
    if (order?.orderNumber) {
      return (
        <Link
          to="#"
          className="py-2 text-center px-4 border-b cursor-pointer underline text-storygreen"
          onClick={() => setSelectedOrderDetails(order)}
        >
          {order?.orderNumber}
        </Link>
      );
    } else return <div className="py-2 text-center px-4 border-b cursor-pointer text-storygreen">-</div>;
  };

  return (
    <div className="p-4">
      {loader ? (
        <LoadingSpinner />
      ) : (
        <div className="flex flex-col">
          <div
            className={`flex items-baseline flex-wrap gap-2 ${selectedUserEmail ? 'justify-between' : 'justify-end'
            }  mb-2`}
          >
            {selectedUserEmail && (
              <button
                type="button"
                className="w-20 flex justify-center bg-storyblueSecondLightText text-white font-bold rounded-md text-base p-2"
                onClick={(e) => {
                  e.stopPropagation();
                  if (selectedOrderDetails) {
                    setSelectedOrderDetails(null);
                  } else {
                    setSelectedUserEmail(null);
                    onOptionClick('Customers');
                  }
                }}
              >
                Back
              </button>
            )}
            <SearchInput placeholder={'Search Order'} />
          </div>
          {selectedUserEmail ? (
            <>
              {selectedOrderDetails && <h1 className="text-xl font-bold">Order - Shipping Details</h1>}
              {user && <h1 className="flex capitalize">Last
                activity: {user.lastSessionActivity && UserTypeActivityTitle[user.lastSessionActivity] || user.lastSessionActivity} - {convertTimestampToDateTime(user?.lastSessionAt)}</h1>}
              <h1 className="flex my-4">Customer Email: {selectedUserEmail}</h1>
              {selectedOrderDetails ? (
                <OrderDetailsTable selectedOrderDetails={selectedOrderDetails} email={selectedUserEmail} />
              ) : order && order?.length > 0 ? (
                <div className="w-full overflow-auto">
                  <table className="min-w-full border border-gray-300">
                    {TableHeader([
                      'Date',
                      'Type',
                      'Item',
                      'Qty',
                      'Order #',
                      'Total',
                      'Country',
                      'Route',
                      'Sync',
                      'Status',
                      ' '
                    ])}
                    <tbody>
                    {order &&
                      order?.map((order, index) => {
                        const physical = isPhysicalOrder(order?.orderType);
                        const sync = getSyncState(order);
                        const route = orderRouteLabel(order);
                        return (
                        <tr key={index} className={index % 2 === 0 ? 'bg-gray-100' : ''}>
                          <td className="py-2 text-left px-4 border-b">
                            {convertTimestampToDateTime(order?.timestamp)}
                          </td>
                          <td className="py-2 text-left px-4 border-b">
                            <span className="inline-block rounded bg-storyblue/10 text-storyblue px-2 py-0.5 text-xs font-semibold">
                              {orderTypeLabel(order?.orderType)}
                            </span>
                          </td>
                          <td className="py-2 text-left px-4 border-b max-w-xs truncate" title={orderItemLabel(order)}>
                            {orderItemLabel(order)}
                          </td>
                          <td className="py-2 text-left px-4 border-b">{order?.quantity}</td>
                          <td className="py-2 text-left px-4 border-b">{orderDetail(order)}</td>
                          <td className="py-2 text-left px-4 border-b">
                            $ {calculateOrderTotal(order)}
                          </td>
                          <td className="py-2 text-left px-4 border-b">
                            {physical ? order?.shippingCountry || '-' : '-'}
                          </td>
                          <td className="py-2 text-left px-4 border-b">
                            {!physical ? (
                              '-'
                            ) : sync === 'pending' ? (
                              // Not sent yet → no route is decided. Show the pending state, not a guessed provider.
                              <span className="text-gray-400" title="Route is assigned when the order is sent to a provider">
                                Pending
                              </span>
                            ) : (
                              <Badge tone={route === 'ShipStation' ? 'gray' : 'amber'}>{route}</Badge>
                            )}
                          </td>
                          <td className="py-2 text-left px-4 border-b">
                            {sync === 'na' || !SYNC_STATE_BADGE[sync]?.tone ? (
                              <span className="text-gray-400">-</span>
                            ) : (
                              <Badge
                                tone={SYNC_STATE_BADGE[sync].tone as Tone}
                                title={
                                  sync === 'synced'
                                    ? 'Sent to fulfillment provider'
                                    : sync === 'error'
                                      ? order?.lastFulfillmentError || 'A send was attempted and failed'
                                      : sync === 'bundle'
                                        ? 'Bundle of individual orders — see their rows for sync status'
                                        : 'Not sent to provider yet'
                                }
                              >
                                {syncStateLabel(sync)}
                              </Badge>
                            )}
                          </td>
                          <td className="py-2 text-left px-4 border-b">
                            {/* Canonical fulfillment status (server-resolved). Non-physical orders keep
                                their legacy status text (subscription/credit states aren't fulfillment). */}
                            {physical ? (
                              <FulfillmentStatusBadges
                                status={order?.fulfillmentStatus}
                                actionNeeded={order?.fulfillmentActionNeeded}
                              />
                            ) : (
                              <span className="capitalize">{(order?.status && order?.status?.replace('_', ' ')) || '-'}</span>
                            )}
                          </td>
                          <td className="py-2 text-left px-4">
                            <div
                              className="flex justify-center items-center text-center gap-2">
                              {physical &&
                                (!order.shipLoading
                                ? <FontAwesomeIcon title="Ship selected book" icon={faBox} onClick={() => ship(order)}
                                                   className="ml-3 mr-3 text-xl text-storyblue p-2 cursor-pointer" /> :
                                <Loader loader={order.shipLoading} dimentions={'w-9 h-9'} className={'px-3'} />)}
                              <FontAwesomeIcon
                                icon={faEye}
                                className="cursor-pointer"
                                title="View Order Details"
                                onClick={() => setSelectedOrderDetails(order)}
                              />
                            </div>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="flex justify-between items-center mt-4">
                    <TableInfoBar
                      currentPage={currentPage}
                      pageSize={25}
                      totalPages={totalPages || 0}
                      recordsOnPage={order?.length || 0}
                      label="orders"
                    />
                    {(totalPages ?? 0) > 1 && (
                      <PaginationButtons
                        totalPages={totalPages!}
                        currentPage={currentPage}
                        handlePageChange={handlePageChange}
                      />
                    )}
                  </div>
                </div>
              ) : (
                <NoItemsFound message="No Orders Found" />
              )}
            </>
          ) : (
            <NoItemsFound message="Select a customer to view their orders." />
          )}
        </div>
      )}
      <Dialogs
        openDialog={viewBooks}
        handelClose={closeDialog}
        handelProcess={() => {
          closeDialog();
        }}
        selectedUser={selectedUser}
      />
    </div>
  );
};
export default PhysicalBookOrder;
